<template>
  <div class="perm-page">
    <header class="page-header">
      <h2 class="page-title">用户、角色及权限</h2>
      <p class="page-desc">维护登录账号与「角色 → 菜单权限」映射；保存后立即作用于对应角色用户。</p>
    </header>

    <el-alert type="info" :closable="false" show-icon class="perm-tip">
      <template #title>说明</template>
      角色固定为三种：领导、助理、管理员。新增账号请指定初始角色与密码；禁用后该用户无法登录。
    </el-alert>

    <div class="toolbar-row">
      <el-button v-if="canManageAccounts" type="primary" @click="handleAddUser">
        <span class="btn-text">添加用户</span>
      </el-button>
      <el-button @click="handleAddRole">关于角色</el-button>
    </div>

    <el-tabs v-model="activeTab" class="perm-tabs" type="border-card">
      <el-tab-pane label="角色权限" name="role">
        <el-card shadow="never" class="inner-card">
          <div class="role-toolbar">
            <div class="role-select-block">
              <span class="field-label">当前配置角色</span>
              <el-radio-group v-model="selectedRole" size="default">
                <el-radio-button
                  v-for="r in ROLE_OPTIONS"
                  :key="r.value"
                  :label="r.value"
                >
                  {{ r.label }}
                </el-radio-button>
              </el-radio-group>
              <p class="role-desc">{{ currentRoleDesc }}</p>
            </div>
            <div class="role-actions">
              <el-button :loading="permLoading" @click="loadRolePermissions">重新加载</el-button>
              <el-button
                v-if="canManageAccounts"
                type="primary"
                :loading="saveLoading"
                @click="handleSavePermission"
              >
                保存权限
              </el-button>
            </div>
          </div>

          <el-divider content-position="left">菜单可见范围</el-divider>

          <div class="perm-grid">
            <div v-for="group in PERMISSION_GROUPS" :key="group.title" class="perm-card">
              <div class="perm-card-head">
                <span class="perm-card-title">{{ group.title }}</span>
                <el-checkbox
                  :model-value="isGroupAllSelected(group)"
                  :indeterminate="isGroupIndeterminate(group)"
                  :disabled="!canManageAccounts"
                  @change="(v) => toggleGroupAll(group, v)"
                >
                  全选本组
                </el-checkbox>
              </div>
              <el-checkbox-group
                v-model="permissions"
                class="perm-card-body"
                :disabled="!canManageAccounts"
              >
                <el-checkbox
                  v-for="item in group.items"
                  :key="item.code"
                  :label="item.code"
                  border
                >
                  {{ item.label }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
          </div>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="用户列表" name="users">
        <el-card shadow="never" class="inner-card">
          <div class="table-toolbar">
            <span class="muted">共 {{ userTableData.length }} 个账号</span>
            <el-button text type="primary" @click="fetchUsers">刷新列表</el-button>
          </div>
          <el-table
            v-loading="usersLoading"
            :data="userTableData"
            stripe
            border
            class="user-table"
            empty-text="暂无用户数据"
          >
            <el-table-column prop="username" label="用户名" min-width="110" show-overflow-tooltip />
            <el-table-column prop="realName" label="真实姓名" min-width="100" show-overflow-tooltip />
            <el-table-column prop="mobile" label="手机" min-width="120" show-overflow-tooltip />
            <el-table-column label="角色" width="100">
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ roleLabel(row.role) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="88">
              <template #default="{ row }">
                <el-tag :type="row.status === 'active' ? 'success' : 'danger'" size="small">
                  {{ row.status === 'active' ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" label="创建时间" width="112" />
            <el-table-column prop="lastLogin" label="最后登录" min-width="130" />
            <el-table-column v-if="canManageAccounts" label="操作" width="168" fixed="right">
              <template #default="{ row }">
                <el-button link type="primary" size="small" @click="handleEditUser(row)">编辑</el-button>
                <el-button
                  link
                  :type="row.status === 'active' ? 'danger' : 'success'"
                  size="small"
                  @click="handleToggleStatus(row)"
                >
                  {{ row.status === 'active' ? '禁用' : '启用' }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <el-dialog v-model="addUserVisible" title="添加用户" width="440px" destroy-on-close>
      <el-form :model="addUserForm" label-width="88px" @submit.prevent>
        <el-form-item label="用户名">
          <el-input v-model="addUserForm.username" placeholder="登录账号" clearable />
        </el-form-item>
        <el-form-item label="真实姓名">
          <el-input v-model="addUserForm.realName" placeholder="展示名称（可选）" clearable />
        </el-form-item>
        <el-form-item label="手机">
          <el-input v-model="addUserForm.mobile" placeholder="可选" maxlength="20" clearable />
        </el-form-item>
        <el-form-item label="角色">
          <el-select v-model="addUserForm.role" style="width: 100%">
            <el-option
              v-for="r in ROLE_OPTIONS"
              :key="r.value"
              :label="r.label"
              :value="r.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="初始密码">
          <el-input v-model="addUserForm.password" type="password" show-password placeholder="至少设置初始密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addUserVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAddUser">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="editUserVisible" title="编辑用户" width="400px" destroy-on-close>
      <el-form :model="editUserForm" label-width="88px">
        <el-form-item label="用户名">
          <el-input v-model="editUserForm.username" disabled />
        </el-form-item>
        <el-form-item label="真实姓名">
          <el-input v-model="editUserForm.realName" disabled />
        </el-form-item>
        <el-form-item label="手机">
          <el-input v-model="editUserForm.mobile" placeholder="可选" maxlength="20" clearable />
        </el-form-item>
        <el-form-item label="角色">
          <el-select v-model="editUserForm.role" style="width: 100%">
            <el-option
              v-for="r in ROLE_OPTIONS"
              :key="r.value"
              :label="r.label"
              :value="r.value"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editUserVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEditUser">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import { ROLE_OPTIONS, PERMISSION_GROUPS, roleLabel } from '@/config/permissionPage.js'

const activeTab = ref('role')
const selectedRole = ref('chairman')
const permissions = ref([])
const userTableData = ref([])
const permLoading = ref(false)
const saveLoading = ref(false)
const usersLoading = ref(false)
/** 与后端一致：is_staff 或角色 admin 可创建/改账号 */
const canManageAccounts = ref(false)

const currentRoleDesc = computed(() => {
  return ROLE_OPTIONS.find((r) => r.value === selectedRole.value)?.desc || ''
})

function isGroupAllSelected(group) {
  if (!group.items.length) return false
  return group.items.every((i) => permissions.value.includes(i.code))
}

function isGroupIndeterminate(group) {
  const n = group.items.filter((i) => permissions.value.includes(i.code)).length
  return n > 0 && n < group.items.length
}

function toggleGroupAll(group, checked) {
  const codes = new Set(group.items.map((i) => i.code))
  const cur = new Set(permissions.value)
  if (checked) {
    codes.forEach((c) => cur.add(c))
  } else {
    codes.forEach((c) => cur.delete(c))
  }
  permissions.value = [...cur]
}

const loadRolePermissions = async () => {
  if (!selectedRole.value) return
  permLoading.value = true
  try {
    const res = await axios.get('/api/auth/role_permissions/', {
      params: { role: selectedRole.value },
    })
    if (res.data.status === 'ok') {
      const data = res.data.data || {}
      permissions.value = data.permissions || []
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '加载角色权限失败')
  } finally {
    permLoading.value = false
  }
}

const handleSavePermission = async () => {
  if (!selectedRole.value) {
    ElMessage.warning('请先选择角色')
    return
  }
  saveLoading.value = true
  try {
    await axios.post('/api/auth/role_permissions/', {
      role: selectedRole.value,
      permissions: permissions.value,
    })
    ElMessage.success('权限已保存')
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '保存权限失败')
  } finally {
    saveLoading.value = false
  }
}

const fetchUsers = async () => {
  usersLoading.value = true
  try {
    const res = await axios.get('/api/auth/users/')
    if (res.data.status === 'ok') {
      userTableData.value = res.data.data || []
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '获取用户列表失败')
  } finally {
    usersLoading.value = false
  }
}

const addUserVisible = ref(false)
const addUserForm = ref({
  username: '',
  realName: '',
  mobile: '',
  role: 'secretary',
  password: '',
})

const handleAddUser = () => {
  addUserForm.value = {
    username: '',
    realName: '',
    mobile: '',
    role: 'secretary',
    password: '',
  }
  addUserVisible.value = true
}

const submitAddUser = async () => {
  const payload = {
    username: addUserForm.value.username,
    realName: addUserForm.value.realName,
    mobile: addUserForm.value.mobile,
    role: addUserForm.value.role,
    password: addUserForm.value.password,
  }
  if (!payload.username || !payload.password) {
    ElMessage.warning('用户名和密码不能为空')
    return
  }
  try {
    const res = await axios.post('/api/auth/users/', payload)
    if (res.data.status === 'ok') {
      ElMessage.success('用户创建成功')
      addUserVisible.value = false
      fetchUsers()
    } else {
      ElMessage.error(res.data.message || '用户创建失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '用户创建失败')
  }
}

const handleAddRole = () => {
  ElMessage.info('系统固定三种角色：领导 / 助理 / 管理员，无需单独「添加角色」。')
}

const editUserVisible = ref(false)
const editUserForm = ref({
  username: '',
  realName: '',
  mobile: '',
  role: '',
  status: '',
})

const handleEditUser = (row) => {
  editUserForm.value = {
    username: row.username,
    realName: row.realName,
    mobile: row.mobile || '',
    role: row.role || 'secretary',
    status: row.status,
  }
  editUserVisible.value = true
}

const handleToggleStatus = (row) => {
  const nextStatus = row.status === 'active' ? 'inactive' : 'active'
  const tip =
    nextStatus === 'inactive'
      ? `确定禁用用户「${row.realName || row.username}」？禁用后将无法登录。`
      : `确定重新启用用户「${row.realName || row.username}」？`
  ElMessageBox.confirm(tip, '确认', {
    type: 'warning',
    confirmButtonText: '确定',
    cancelButtonText: '取消',
  })
    .then(() =>
      axios.post('/api/auth/user_update/', {
        username: row.username,
        status: nextStatus,
      })
    )
    .then((res) => {
      if (res.data.status === 'ok') {
        row.status = nextStatus
        ElMessage.success(nextStatus === 'active' ? '已启用' : '已禁用')
      } else {
        ElMessage.error(res.data.message || '状态更新失败')
      }
    })
    .catch((e) => {
      if (e === 'cancel') return
      ElMessage.error(e.response?.data?.message || '状态更新失败')
    })
}

watch(selectedRole, () => {
  loadRolePermissions()
})

async function loadCurrentUserCaps() {
  try {
    const res = await axios.get('/api/auth/me/')
    if (res.data.status === 'ok') {
      const d = res.data.data || {}
      canManageAccounts.value = Boolean(d.isStaff) || d.role === 'admin'
    }
  } catch {
    canManageAccounts.value = false
  }
}

onMounted(async () => {
  await loadCurrentUserCaps()
  fetchUsers()
  loadRolePermissions()
})

const submitEditUser = async () => {
  try {
    const res = await axios.post('/api/auth/user_update/', {
      username: editUserForm.value.username,
      role: editUserForm.value.role,
      mobile: editUserForm.value.mobile,
    })
    if (res.data.status === 'ok') {
      const target = userTableData.value.find((u) => u.username === editUserForm.value.username)
      if (target) {
        target.role = editUserForm.value.role
        target.mobile = editUserForm.value.mobile
      }
      ElMessage.success('已保存')
      editUserVisible.value = false
    } else {
      ElMessage.error(res.data.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || '保存失败')
  }
}
</script>

<style scoped>
.perm-page {
  max-width: 1200px;
}

.page-header {
  margin-bottom: 16px;
}

.perm-tip {
  margin-bottom: 16px;
}

.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  align-items: center;
}

.btn-text {
  font-weight: 500;
}

.perm-tabs {
  border-radius: 8px;
  overflow: hidden;
}

.perm-tabs :deep(.el-tabs__content) {
  padding: 0;
}

.inner-card {
  border: none;
}

.role-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  align-items: flex-start;
  justify-content: space-between;
}

.role-select-block {
  flex: 1;
  min-width: 260px;
}

.field-label {
  display: block;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 8px;
}

.role-desc {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.45);
}

.role-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.perm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}

.perm-card {
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  padding: 12px 14px;
  background: var(--el-fill-color-blank);
}

.perm-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--el-border-color-extra-light);
}

.perm-card-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

.perm-card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.perm-card-body :deep(.el-checkbox) {
  margin-right: 0;
  height: auto;
  white-space: normal;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.muted {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
}

.user-table {
  width: 100%;
}
</style>
